import { PropsWithChildren, RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { CaretDownIcon } from '@verbit-ai/icons-library'
import { UseDisclosureReturn, useTheme } from '@chakra-ui/react'
import { Drawer, Popover } from '@verbit-ai/verbit-ui-library'
import { useMediaPlayerContext } from '@verbit-ai/verbit-connect-web/player-provider'

import { analyticsService } from 'src/services/AnalyticsService'
import { useUserContext } from 'src/contexts/UserProvider'
import { fuzzySearch } from 'src/utils/search'
import { useTranscriptClient } from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptHighLight } from 'src/contexts/TranscriptHighLightContext'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'
import { announceText } from 'src/utils/announce'
import { ParagraphTextRange } from 'src/models/Text'

import { SearchField } from './SearchField'
import * as S from './styles'

type SearchProps = PropsWithChildren & Pick<UseDisclosureReturn, 'isOpen' | 'onOpen' | 'onClose'>

export const Search = (props: SearchProps) => {
    const { isOpen, onOpen, onClose, children } = props
    const { user } = useUserContext()
    const transcriptClient = useTranscriptClient()
    const { isMediumContainer, containerRef } = useResponsiveContainer()
    const { selectTextToHighlight, clearSearchHighlight } = useTranscriptHighLight()
    const { mediaPlayer } = useMediaPlayerContext()

    const [searchTerm, setSearchTerm] = useState('')
    const [results, setResults] = useState<ParagraphTextRange[]>([])
    const [activeItem, setActiveItem] = useState(0)
    const previousSearchTerm = useRef<string | undefined>(undefined)

    useEffect(() => {
        if (results.length) {
            announceText(`Result number ${activeItem + 1} out of ${results.length}`)
        }
    }, [activeItem, results.length])

    const {
        palette: {
            search: { navButtonColor },
        },
    } = useTheme()

    const onSearchTermChange = useCallback(
        (value: string) => {
            setSearchTerm(value)
            // when input is cleared, reset results automatically
            // (so that user doesn't have to hit Enter after clearing)
            if (!value.length) {
                setResults([])
                clearSearchHighlight()
                previousSearchTerm.current = undefined
            }
        },
        [clearSearchHighlight],
    )

    const onActiveItemChange = useCallback(
        (delta: 1 | -1) => {
            let nextActiveItem = activeItem + delta
            if (nextActiveItem < 0) {
                nextActiveItem = results.length - 1
            }
            if (nextActiveItem >= results.length) {
                nextActiveItem = 0
            }
            setActiveItem(nextActiveItem)
            selectTextToHighlight(results[nextActiveItem])
        },
        [activeItem, results, selectTextToHighlight],
    )
    const prevItem = useCallback(() => onActiveItemChange(-1), [onActiveItemChange])
    const nextItem = useCallback(() => onActiveItemChange(1), [onActiveItemChange])

    const onSearch = useCallback(
        (newSearchTerm: string) => {
            if (newSearchTerm.length) {
                // pressing Enter multiple times with the same term
                // cycles through results instead of doing new search
                if (newSearchTerm === previousSearchTerm.current) {
                    onActiveItemChange(1)

                    return
                }
                analyticsService.trackSearch({ user, query: newSearchTerm })
                const searchResults = fuzzySearch(transcriptClient, newSearchTerm)
                setResults(searchResults)
                setActiveItem(0)
                selectTextToHighlight(searchResults[0])
                previousSearchTerm.current = newSearchTerm
            } else {
                setResults([])
                clearSearchHighlight()
            }
        },
        [user, transcriptClient, selectTextToHighlight, clearSearchHighlight, onActiveItemChange],
    )

    useEffect(() => {
        if (isOpen) {
            mediaPlayer.pause()
        } else {
            setSearchTerm('')
            setResults([])
            setActiveItem(0)
            clearSearchHighlight()
            previousSearchTerm.current = undefined
        }
    }, [isOpen, mediaPlayer, clearSearchHighlight])

    // Search can unmount while open (toolbar swap, empty transcript) without ever closing.
    useEffect(() => clearSearchHighlight, [clearSearchHighlight])

    const inputRef = useRef<HTMLInputElement | null>(null)
    const searchPanel = () => (
        <S.SearchPanel>
            <SearchField
                inputRef={inputRef}
                value={searchTerm}
                onChange={onSearchTermChange}
                onSearch={onSearch}
                activeIndex={activeItem}
            />
            <S.SearchChevron className={S.ROTATED_CLASSNAME} tabIndex={0} aria-label="Previous" onClick={prevItem}>
                <CaretDownIcon color={navButtonColor} />
            </S.SearchChevron>
            <S.SearchChevron tabIndex={0} aria-label="Next" onClick={nextItem}>
                <CaretDownIcon color={navButtonColor} />
            </S.SearchChevron>
            <S.SearchResultsFound>
                {results.length ? activeItem + 1 : 0} of {results.length}
            </S.SearchResultsFound>
        </S.SearchPanel>
    )

    return isMediumContainer ? (
        <Drawer isOpen={isOpen} onClose={onClose} placement="top" portalProps={{ containerRef }}>
            {searchPanel()}
        </Drawer>
    ) : (
        <Popover
            pattern="disclosure"
            contentAriaLabel="Search in transcript"
            usePortal
            triggerEl={children}
            trigger="click"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            placement="top-start"
            hasArrow
            boxShadow="verbit_floating_action_button_shadow"
            contentWidth="unset"
            initialFocusRef={inputRef as RefObject<{ focus(): void }>}>
            {searchPanel()}
        </Popover>
    )
}
